import type {
  DiversificationConstraint,
  ModelConstraint,
} from '@/types/api/constraints'
import type { SyntheticModelConstraint } from '@/types/api/synthetic-target'

import { DiversificationConstraintType } from '@moment/horizon-data-access/proposal'
import isNil from 'lodash/isNil'

import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import {
  type DiversificationParameterConstraintKey,
  type DiversificationParameterConstraints,
  Field,
  UniqueConstraintKey,
} from '@/portfolio/proposal-builder/types'

/**
 * Serialize a diversification constraint, synthesizing
 * `max_weight_per_unique_value: 1` only when no backend-required field is
 * already populated. Backend-required fields are:
 *   min/max_market_value_per_unique_value
 *   min/max_weight_per_unique_value
 *   min/max_unique_fields
 *   min/max_instruments_per_unique_value
 */
function serializeWithBackendRequiredField(
  constraint: DiversificationConstraint,
): SyntheticModelConstraint {
  const hasBackendRequiredField =
    !isNil(constraint.min_market_value_per_unique_value) ||
    !isNil(constraint.max_market_value_per_unique_value) ||
    !isNil(constraint.min_weight_per_unique_value) ||
    !isNil(constraint.max_weight_per_unique_value) ||
    !isNil(constraint.min_unique_fields) ||
    !isNil(constraint.max_unique_fields) ||
    !isNil(constraint.min_instruments_per_unique_value) ||
    !isNil(constraint.max_instruments_per_unique_value)

  if (hasBackendRequiredField) {
    return { id: generateId(), constraint_priority: 1, ...constraint }
  }
  return {
    id: generateId(),
    constraint_priority: 1,
    max_weight_per_unique_value: 1,
    ...constraint,
  }
}

const FIELD_TO_UNIQUE_CONSTRAINT_KEY: Partial<
  Record<string, `${UniqueConstraintKey}`>
> = {
  [Field.Isin]: UniqueConstraintKey.UniqueBonds,
  [Field.Cusip]: UniqueConstraintKey.UniqueBonds,
  [Field.Issuer]: UniqueConstraintKey.UniqueIssuers,
  [Field.UltimateParent]: UniqueConstraintKey.UniqueUltimateParents,
  [Field.UltimateBorrower]: UniqueConstraintKey.UniqueUltimateBorrowers,
}

/**
 * Creates empty diversification parameter constraints with no defaults.
 */
export function createEmptyDiversificationParameterConstraints(): DiversificationParameterConstraints {
  return {
    weightPerBond: undefined,
    maxBondsPerIssuer: undefined,
    maxBondsPerUltimateParent: undefined,
    maxBondsPerUltimateBorrower: undefined,
    uniqueBonds: undefined,
    uniqueIssuers: undefined,
    uniqueUltimateParents: undefined,
    uniqueUltimateBorrowers: undefined,
  }
}

const FIELD_TO_MAX_BONDS_CONSTRAINT_KEY: Partial<
  Record<string, DiversificationParameterConstraintKey>
> = {
  [Field.Issuer]: 'maxBondsPerIssuer',
  [Field.UltimateParent]: 'maxBondsPerUltimateParent',
  [Field.UltimateBorrower]: 'maxBondsPerUltimateBorrower',
}

/**
 * The diversification-parameter slot a constraint belongs to, or undefined
 * when it isn't slot-shaped and should live in the risk-factor tree /
 * otherConstraints instead.
 */
function matchDiversificationParameterSlot(
  constraint: ModelConstraint,
): DiversificationParameterConstraintKey | undefined {
  if (constraint.type !== DiversificationConstraintType.diversification)
    return undefined
  const field = constraint.field.toLowerCase()

  if (
    !isNil(constraint.min_unique_fields) ||
    !isNil(constraint.max_unique_fields)
  ) {
    return FIELD_TO_UNIQUE_CONSTRAINT_KEY[field]
  }

  if (
    (field === Field.Isin || field === Field.Cusip) &&
    (!isNil(constraint.min_weight_per_unique_value) ||
      !isNil(constraint.max_weight_per_unique_value) ||
      !isNil(constraint.min_market_value_per_unique_value) ||
      !isNil(constraint.max_market_value_per_unique_value))
  ) {
    return 'weightPerBond'
  }

  if (!isNil(constraint.max_instruments_per_unique_value)) {
    return FIELD_TO_MAX_BONDS_CONSTRAINT_KEY[field]
  }

  return undefined
}

/**
 * Normalizes a weight-per-bond constraint: cusip → isin (legacy templates may
 * use cusip), and when both MV and weight halves are populated (legacy/wonky
 * data) prefer MV and clear the weight half so the in-memory state matches a
 * single mode.
 */
function normalizeWeightPerBond(
  constraint: DiversificationConstraint,
): DiversificationConstraint {
  const fieldNormalized =
    constraint.field.toLowerCase() === Field.Cusip
      ? { ...constraint, field: Field.Isin }
      : constraint

  const hasMarketValue =
    !isNil(fieldNormalized.min_market_value_per_unique_value) ||
    !isNil(fieldNormalized.max_market_value_per_unique_value)
  const hasWeight =
    !isNil(fieldNormalized.min_weight_per_unique_value) ||
    !isNil(fieldNormalized.max_weight_per_unique_value)

  return hasMarketValue && hasWeight
    ? {
        ...fieldNormalized,
        min_weight_per_unique_value: undefined,
        max_weight_per_unique_value: undefined,
      }
    : fieldNormalized
}

/**
 * Removes the slot constraint matching the given id, if any. Returns whether
 * a slot was cleared.
 */
export function removeDiversificationParameterConstraintById(
  diversificationParameterConstraints: DiversificationParameterConstraints,
  constraintId: string,
): boolean {
  for (const key of Object.keys(
    diversificationParameterConstraints,
  ) as DiversificationParameterConstraintKey[]) {
    if (diversificationParameterConstraints[key]?.id === constraintId) {
      diversificationParameterConstraints[key] = undefined
      return true
    }
  }
  return false
}

/**
 * Places a constraint into its diversification-parameter slot when it is
 * slot-shaped and the slot is still empty. Returns whether it was placed.
 */
export function placeDiversificationParameterConstraint(
  diversificationParameterConstraints: DiversificationParameterConstraints,
  constraint: ModelConstraint,
): boolean {
  const key = matchDiversificationParameterSlot(constraint)
  if (
    key === undefined ||
    constraint.type !== DiversificationConstraintType.diversification ||
    diversificationParameterConstraints[key] !== undefined
  ) {
    return false
  }
  diversificationParameterConstraints[key] =
    key === 'weightPerBond' ? normalizeWeightPerBond(constraint) : constraint
  return true
}

/**
 * Extracts diversification parameter constraints from model constraints.
 * Returns the extracted constraints and remaining constraints that weren't
 * extracted. Each slot takes the first matching constraint; later matches are
 * left in the list so they surface as regular rules instead of being dropped.
 */
export function extractDiversificationParameterConstraints(
  constraints: ModelConstraint[],
): {
  diversificationParameterConstraints: DiversificationParameterConstraints
  diversificationParameterConstraintIds: Set<string>
} {
  const diversificationParameterConstraintIds = new Set<string>()
  const diversificationParameterConstraints =
    createEmptyDiversificationParameterConstraints()

  for (const constraint of constraints) {
    if (
      placeDiversificationParameterConstraint(
        diversificationParameterConstraints,
        constraint,
      )
    ) {
      diversificationParameterConstraintIds.add(constraint.id ?? '')
    }
  }

  return {
    diversificationParameterConstraints,
    diversificationParameterConstraintIds,
  }
}

/**
 * Converts diversification parameter constraints to model constraints for the API request.
 */
export function diversificationParameterConstraintsToModelConstraints(
  diversificationParameterConstraints: DiversificationParameterConstraints,
): SyntheticModelConstraint[] {
  const constraints: SyntheticModelConstraint[] = []

  if (diversificationParameterConstraints.weightPerBond) {
    constraints.push(
      serializeWithBackendRequiredField(
        diversificationParameterConstraints.weightPerBond,
      ),
    )
  }

  if (diversificationParameterConstraints.maxBondsPerIssuer) {
    constraints.push(
      serializeWithBackendRequiredField(
        diversificationParameterConstraints.maxBondsPerIssuer,
      ),
    )
  }

  if (diversificationParameterConstraints.maxBondsPerUltimateParent) {
    constraints.push(
      serializeWithBackendRequiredField(
        diversificationParameterConstraints.maxBondsPerUltimateParent,
      ),
    )
  }

  if (diversificationParameterConstraints.maxBondsPerUltimateBorrower) {
    constraints.push(
      serializeWithBackendRequiredField(
        diversificationParameterConstraints.maxBondsPerUltimateBorrower,
      ),
    )
  }

  if (diversificationParameterConstraints.uniqueBonds) {
    constraints.push({
      id: generateId(),
      constraint_priority: 1,
      ...diversificationParameterConstraints.uniqueBonds,
    })
  }

  if (diversificationParameterConstraints.uniqueIssuers) {
    constraints.push({
      id: generateId(),
      constraint_priority: 1,
      ...diversificationParameterConstraints.uniqueIssuers,
    })
  }

  if (diversificationParameterConstraints.uniqueUltimateParents) {
    constraints.push({
      id: generateId(),
      constraint_priority: 1,
      ...diversificationParameterConstraints.uniqueUltimateParents,
    })
  }

  if (diversificationParameterConstraints.uniqueUltimateBorrowers) {
    constraints.push({
      id: generateId(),
      constraint_priority: 1,
      ...diversificationParameterConstraints.uniqueUltimateBorrowers,
    })
  }

  return constraints
}

// The builders below mint an id so a slot constraint has a stable identity
// from the moment the sidepane creates it. Callers overwrite it with the loaded
// id when one exists. Without an id, edits routed through the store's
// placeConstraint skip its remove-then-replace step (gated on `constraint.id`)
// and duplicate the rule into the risk-factor tree instead of updating the slot.
export function buildWeightPerBondConstraint(): DiversificationConstraint {
  return {
    id: generateId(),
    type: DiversificationConstraintType.diversification,
    field: Field.Isin,
  }
}

export function buildMaxBondsConstraint(
  field: Field,
  max: number,
): DiversificationConstraint {
  return {
    id: generateId(),
    type: DiversificationConstraintType.diversification,
    field,
    max_instruments_per_unique_value: max,
  }
}

export function buildUniqueFieldsConstraint({
  field,
  minUniqueFields,
  maxUniqueFields,
}: {
  field: Field
  minUniqueFields?: number
  maxUniqueFields?: number
}): DiversificationConstraint {
  return {
    id: generateId(),
    type: DiversificationConstraintType.diversification,
    field,
    min_unique_fields: minUniqueFields,
    max_unique_fields: maxUniqueFields,
  }
}
